import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { ease } from "@/components/Reveal";

const WORD = "CASABLANCA".split("");

export function Hero() {
  const { t } = useStore();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const shoeY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const wordY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 80, damping: 16 });
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 80, damping: 16 });
  const shadowX = useSpring(useTransform(mx, [-0.5, 0.5], [30, -30]), { stiffness: 80, damping: 16 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative flex min-h-[calc(100svh-6rem)] flex-col items-center overflow-hidden bg-sun pt-10 text-ink sm:pt-14" data-testid="hero-section">
      <div className="container-x flex w-full justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-ink/60">
        <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}>{t("heroEyebrow")}</motion.span>
        <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }} className="hidden sm:inline">EU · EUR</motion.span>
      </div>

      <motion.div style={{ y: wordY }} className="relative z-10 mt-6 text-center sm:mt-8">
        <h1 className="wordmark flex justify-center text-[10.6vw] leading-[0.9] tracking-[0.02em] sm:tracking-[0.04em] lg:text-[min(9.5vw,19vh)]" aria-label="CASABLANCA" data-testid="hero-title">
          {WORD.map((ch, i) => (
            <span key={i} className="inline-block overflow-hidden">
              <motion.span className="inline-block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ delay: 0.15 + i * 0.05, duration: 1, ease }}>{ch}</motion.span>
            </span>
          ))}
        </h1>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8, ease }}>
          <Link to="/shop" className="link-u mt-3 inline-block pl-[0.9em] font-mono text-sm font-medium uppercase tracking-[0.9em] sm:text-lg" data-testid="hero-shop-link">SHOP</Link>
        </motion.div>
      </motion.div>

      <motion.div style={{ y: shoeY }} className="relative mt-[2vh] flex w-[min(940px,92vw,calc((100svh-27rem)*1.81))] min-w-[330px] flex-1 items-center">
        <div style={{ perspective: 1400 }} className="relative w-full pb-10">
          <motion.div initial={{ opacity: 0, y: 90, scale: 0.94 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ delay: 0.5, duration: 1.6, ease }}>
            <motion.div style={{ rotateX: rotX, rotateY: rotY }} className="animate-floaty">
              <img src="/img/hero-lavender-cut.webp" alt="CASABLANCA Lavender Study — studio sneaker concept in electric lavender suede" fetchpriority="high"
                className="mx-auto w-full select-none drop-shadow-[0_40px_40px_rgba(28,26,25,0.18)]" data-testid="hero-sneaker" />
            </motion.div>
          </motion.div>
          <motion.div aria-hidden style={{ x: shadowX }} className="pointer-events-none absolute bottom-2 left-1/2 h-7 w-[62%] -translate-x-1/2 rounded-[50%] bg-ink/30 blur-2xl" />
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 0.8 }}
        className="container-x relative z-10 flex w-full flex-col items-center justify-between gap-4 pb-8 sm:flex-row sm:items-end">
        <div className="text-center sm:text-left">
          <p className="font-display text-sm font-black uppercase tracking-tight sm:text-base" data-testid="hero-headline">STEP INTO YOUR NEXT ICON.</p>
          <p className="mt-1 max-w-xs text-xs text-ink/60" data-testid="hero-subtitle">{t("heroSub")}</p>
        </div>
        <p className="hidden font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50 lg:block">Lavender Study — studio concept</p>
        <Link to="/collections" className="link-u flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.25em]" data-testid="hero-explore-btn">
          {t("exploreCollections")} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </motion.div>
    </section>
  );
}
